import { useCallback, useEffect, useRef } from "react";
import type { BaseEntity, PaginationState } from "../../UniversalCRMTable/types";
import type { EntityListData } from "../types";

interface UseInfiniteScrollProps<T extends BaseEntity> {
  data: EntityListData<T> | undefined;
  pagination: PaginationState;
  accumulatedData: T[];
  setAccumulatedData: (data: T[] | ((prev: T[]) => T[])) => void;
  /** Filter context — accepted for call-site parity; not read in this hook. */
  filterContext?: unknown;
}

/**
 * Manages infinite scroll accumulation and pagination
 * Handles: deduplication, cursor-based pagination, loadMore callback
 */
export function useInfiniteScroll<T extends BaseEntity>({
  data,
  pagination,
  accumulatedData,
  setAccumulatedData,
  filterContext,
}: UseInfiniteScrollProps<T>) {
  // Track the last accumulated data set to avoid redundant deduplication
  const lastAccumulatedDataRef = useRef<T[]>([]);
  /** First page rows while cursor is null — needed so page 2+ merges onto page 1 instead of replacing it. */
  const firstPageSnapshotRef = useRef<T[]>([]);

  useEffect(() => {
    if (!pagination.cursor && data?.data && data.data.length > 0) {
      firstPageSnapshotRef.current = data.data as T[];
    }
  }, [pagination.cursor, data?.data]);

  // For infinite scroll, use accumulated data when we have a cursor.
  // Fall back to firstPageSnapshotRef when accumulatedData is empty but cursor is set
  // (i.e., the moment cursor changes but the new page hasn't arrived yet). Without this
  // fallback, Apollo returns undefined for the new cursor variables (cache miss) and
  // data?.data becomes undefined, causing the card grid to flash empty.
  const displayData = pagination.cursor
    ? accumulatedData.length > 0
      ? accumulatedData
      : firstPageSnapshotRef.current.length > 0
        ? firstPageSnapshotRef.current
        : data?.data || []
    : data?.data || [];

  // Memoize the deduplication logic to avoid creating new Sets on every render
  const deduplicateAndAccumulate = useCallback((newData: T[], prevAccumulated: T[]) => {
    if (!newData || newData.length === 0) return prevAccumulated;

    // Create a Set of existing IDs for O(1) lookup
    const existingIds = new Set(prevAccumulated.map((item) => item.id));

    // Filter out duplicates
    const newItems = newData.filter((item) => !existingIds.has(item.id));

    if (process.env.NODE_ENV === "development") {
      console.debug("[InfiniteScroll] Data accumulation", {
        prevCount: prevAccumulated.length,
        newCount: newData.length,
        duplicatesFiltered: newData.length - newItems.length,
        totalAfterAccumulation: prevAccumulated.length + newItems.length,
      });
    }

    // Return accumulated data with new items appended
    return newItems.length > 0 ? [...prevAccumulated, ...newItems] : prevAccumulated;
  }, []);

  // When cursor changes (loadMore is called), accumulate new data
  useEffect(() => {
    const pageRows = data?.data;
    if (pagination.cursor && pageRows && pageRows.length > 0) {
      // Only update if data has actually changed (not just reference change)
      if (pageRows !== lastAccumulatedDataRef.current) {
        setAccumulatedData((prev) => {
          const base =
            prev.length > 0
              ? prev
              : firstPageSnapshotRef.current.length > 0
                ? [...firstPageSnapshotRef.current]
                : [];
          const updated = deduplicateAndAccumulate(pageRows, base);
          lastAccumulatedDataRef.current = pageRows;
          return updated;
        });
      }
    }
  }, [pagination.cursor, data?.data, deduplicateAndAccumulate, setAccumulatedData]);

  // Implement load more function for infinite scroll
  // Returns the next cursor if available (caller will update pagination)
  const loadMore = useCallback(() => {
    // Get the cursor for the next page from pageInfo
    const nextCursor = data?.pageInfo?.endCursor;
    return nextCursor && data?.pageInfo?.hasNextPage ? nextCursor : null;
  }, [data?.pageInfo?.endCursor, data?.pageInfo?.hasNextPage]);

  return {
    displayData,
    loadMore,
    hasNextPage: data?.pageInfo?.hasNextPage || false,
  };
}
